import { Controller } from "@hotwired/stimulus"

// Item-based multi-file upload for manage/photos. Each item row holds one
// file plus optional per-photo title/tags; the batch fields apply to every
// photo. Rows are posted one request at a time so a large batch never
// outlives the proxy response timeout; with JS disabled the server-rendered
// row still submits as a plain multipart POST (items[]).
export default class extends Controller {
  static targets = ["items", "template", "item", "results", "submit"]

  addItem() {
    this.itemsTarget.appendChild(this.templateTarget.content.cloneNode(true))
  }

  removeItem(event) {
    const row = event.target.closest('[data-bulk-upload-target="item"]')
    this.revokePreview(row)
    row.remove()
  }

  // Shows a thumbnail of the picked file so it can be recognized and tagged.
  preview(event) {
    const row = event.target.closest('[data-bulk-upload-target="item"]')
    const image = row.querySelector('[data-role="preview"]')
    const file = event.target.files[0]

    this.revokePreview(row)

    if (file) {
      image.src = URL.createObjectURL(file)
      image.classList.remove("hidden")
    } else {
      image.removeAttribute("src")
      image.classList.add("hidden")
    }
  }

  revokePreview(row) {
    const image = row.querySelector('[data-role="preview"]')
    if (image?.src.startsWith("blob:")) URL.revokeObjectURL(image.src)
  }

  async submit(event) {
    event.preventDefault()

    const rows = this.itemTargets.filter((row) => !row.dataset.uploaded && this.fileFor(row))
    if (rows.length === 0) {
      this.resultsTarget.innerHTML = ""
      this.appendResult("No files selected.")
      return
    }

    this.submitTarget.disabled = true
    this.resultsTarget.innerHTML = ""

    let uploaded = 0

    for (const row of rows) {
      const status = row.querySelector('[data-role="status"]')
      status.textContent = "⬆️ uploading…"

      try {
        const response = await fetch(this.element.action, {
          method: "POST",
          headers: {
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
            Accept: "application/json",
          },
          body: this.formDataFor(row),
        })
        const data = await response.json().catch(() => ({}))

        if (response.ok && data.ok) {
          uploaded += 1
          row.dataset.uploaded = "true"
          status.textContent = "✅ uploaded"
        } else {
          status.textContent = `❌ ${data.error || `HTTP ${response.status}`}`
        }
      } catch (error) {
        status.textContent = `❌ ${error.message}`
      }
    }

    this.appendResult(`Done: ${uploaded}/${rows.length} uploaded. Items marked ✅ are skipped if you submit again.`)
    this.submitTarget.disabled = false
  }

  fileFor(row) {
    return row.querySelector('input[type="file"]')?.files[0]
  }

  // Batch fields plus exactly one row's file/title/tags per request; every
  // items[...] entry is stripped so other rows aren't re-sent each time.
  formDataFor(row) {
    const formData = new FormData(this.element)
    formData.delete("items[][image]")
    formData.delete("items[][title]")
    formData.delete("items[][tags]")
    formData.append("image", this.fileFor(row))
    formData.append("title", row.querySelector('input[name="items[][title]"]')?.value || "")
    formData.append("item_tags", row.querySelector('input[name="items[][tags]"]')?.value || "")
    return formData
  }

  appendResult(text) {
    const item = document.createElement("li")
    item.textContent = text
    this.resultsTarget.appendChild(item)
    return item
  }
}
